/**
 * ItemRow — fila editable de un artículo dentro de un paquete.
 *
 * Decisiones de diseño:
 * - Flujo 100% teclado (principio 9): Tab recorre los campos de izquierda a
 *   derecha, ENTER en cualquier campo pide al padre una fila nueva en el mismo
 *   paquete, ESC cancela la fila si es nueva y aún no tiene descripción.
 * - description se recorta A 60 CARACTERES AL ESCRIBIR (límite de
 *   almacenamiento del Anexo — generateAnexoPDF.js); el PDF luego muestra 42.
 * - quantity entero mínimo 1 y unit_value flotante ≥ 0 con defaults del brief;
 *   el total es derivado y solo lectura (mono, 2 decimales).
 * - Condición como par de botones NUEVO/USADO con aria-pressed, siguiendo el
 *   patrón de EncomiendaWizard — no un select: dos taps máximo.
 * - Procedencia siempre visible (principio 16): ProvenanceIndicator salta al
 *   mensaje de WhatsApp origen; artículos manuales se marcan en ámbar.
 * - "Pegar fuente" solo aparece cuando hay una cita copiada del hilo: pega el
 *   texto del cliente y enlaza source_message_id (el cliente es la verdad).
 * - Eliminar con confirmación de dos taps inline (armar → confirmar): evita un
 *   modal (reservados a acciones terminales) sin permitir borrados accidentales.
 */
import { useEffect, useRef, useState } from 'react';
import { Trash2, ClipboardPaste, Check } from 'lucide-react';
import ProvenanceIndicator from './shared/ProvenanceIndicator';
import AskClientButton from './shared/AskClientButton';

const MAX_DESC = 60;

const inputBase =
  'rounded-md border px-2 py-1.5 text-xs focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-[var(--blue)] disabled:opacity-60';

const inputStyle = {
  background: 'var(--input-bg)',
  borderColor: 'var(--border)',
  color: 'var(--text-main)',
};

const ItemRow = ({
  item,
  sourceTimestamp,
  onChange,
  onQuitar,
  onEnterNewRow,
  quoteBuffer,
  onConsumeQuote,
  onJumpToMessage,
  onAskClient,
  autoFocus,
  isNew,
}) => {
  const [confirmarQuitar, setConfirmarQuitar] = useState(false);
  const [preguntaEnviada, setPreguntaEnviada] = useState(false);
  const descRef = useRef(null);

  useEffect(() => {
    if (autoFocus) descRef.current?.focus();
  }, [autoFocus]);

  // Desarmar la confirmación de borrado si el operador sigue trabajando.
  useEffect(() => {
    if (!confirmarQuitar) return undefined;
    const t = setTimeout(() => setConfirmarQuitar(false), 4000);
    return () => clearTimeout(t);
  }, [confirmarQuitar]);

  const qty = parseInt(item.quantity) || 1;
  const unitario = parseFloat(item.unit_value) || 0;
  const total = qty * unitario;

  const onKeyDownFila = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnterNewRow && onEnterNewRow();
    } else if (e.key === 'Escape') {
      if (isNew && !(item.description || '').trim()) {
        e.stopPropagation();
        onQuitar && onQuitar();
      } else {
        e.currentTarget.blur?.();
      }
    }
  };

  const pegarFuente = () => {
    if (!quoteBuffer) return;
    onChange('description', String(quoteBuffer.text || '').slice(0, MAX_DESC));
    onChange('source_message_id', quoteBuffer.messageId);
    onChange('no_client_reference', false);
    onConsumeQuote && onConsumeQuote();
  };

  const botonCondicion = (valor) => {
    const activo = (item.condition || 'NUEVO') === valor;
    return (
      <button
        type="button"
        onClick={() => onChange('condition', valor)}
        onKeyDown={onKeyDownFila}
        aria-pressed={activo}
        aria-label={`Condición ${valor === 'NUEVO' ? 'nuevo' : 'usado'}`}
        className="rounded-md border px-2 py-1 text-[10px] font-semibold
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]
                   hover:opacity-90 disabled:opacity-40"
        style={
          activo
            ? valor === 'NUEVO'
              ? { background: 'var(--avianca-red)', borderColor: 'var(--avianca-red)', color: '#fff' }
              : { background: 'var(--text-secondary)', borderColor: 'var(--text-secondary)', color: '#fff' }
            : { background: 'transparent', borderColor: 'var(--border)', color: 'var(--text-secondary)' }
        }
      >
        {valor}
      </button>
    );
  };

  return (
    <div
      className="grid grid-cols-[minmax(0,1fr)_104px_56px_88px_auto_72px_auto] items-center gap-2 rounded-md px-1.5 py-1
                 hover:bg-[var(--nav-active-bg)]"
      role="group"
      aria-label={`Artículo: ${item.description || 'nuevo artículo'}`}
    >
      {/* Descripción + procedencia */}
      <div className="flex min-w-0 items-center gap-1.5">
        <input
          ref={descRef}
          type="text"
          value={item.description || ''}
          onChange={(e) => onChange('description', e.target.value.slice(0, MAX_DESC))}
          onKeyDown={onKeyDownFila}
          placeholder="Descripción del artículo"
          aria-label="Descripción del artículo"
          title={item.description || ''}
          maxLength={MAX_DESC}
          className={`${inputBase} w-full min-w-0`}
          style={inputStyle}
        />
        {quoteBuffer && (
          <button
            type="button"
            onClick={pegarFuente}
            aria-label="Pegar la frase del cliente como descripción"
            title="Pegar fuente"
            className="inline-flex items-center gap-1 rounded-md border px-1.5 py-1 text-[10px] font-medium
                       hover:bg-[var(--nav-active-bg)]
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
            style={{ borderColor: 'var(--blue)', color: 'var(--blue-text)', background: 'var(--blue-soft)' }}
          >
            <ClipboardPaste size={11} aria-hidden="true" />
            Pegar fuente
          </button>
        )}
      </div>

      {/* Procedencia — columna propia para que el encabezado registre con los datos */}
      <div className="flex min-w-0 items-center">
        <ProvenanceIndicator
          sourceMessageId={item.source_message_id}
          timestamp={sourceTimestamp}
          isManual={item.no_client_reference}
          onJump={onJumpToMessage}
        />
      </div>

      {/* Cantidad */}
      <input
        type="number"
        min={1}
        step={1}
        value={qty}
        onChange={(e) => onChange('quantity', Math.max(1, parseInt(e.target.value) || 1))}
        onKeyDown={onKeyDownFila}
        aria-label="Cantidad"
        className={`${inputBase} w-full text-right font-mono`}
        style={inputStyle}
      />

      {/* Valor unitario */}
      <input
        type="number"
        min={0}
        step={0.01}
        value={item.unit_value ?? 0}
        onChange={(e) => onChange('unit_value', Math.max(0, parseFloat(e.target.value) || 0))}
        onKeyDown={onKeyDownFila}
        aria-label="Valor unitario en dólares"
        className={`${inputBase} w-full text-right font-mono`}
        style={inputStyle}
      />

      {/* Condición */}
      <div className="flex gap-1" role="group" aria-label="Condición del artículo">
        {botonCondicion('NUEVO')}
        {botonCondicion('USADO')}
      </div>

      {/* Total derivado */}
      <span
        className="text-right font-mono text-xs font-semibold"
        style={{ color: 'var(--text-main)' }}
        aria-label={`Total del artículo: ${total.toFixed(2)} dólares`}
      >
        {total.toFixed(2)}
      </span>

      {/* Acciones de fila */}
      <div className="flex items-center gap-0.5">
        <AskClientButton
          compact
          fieldLabel={`Artículo: ${item.description || 'nuevo artículo'}`}
          defaultMessage={`Confirmame por favor: ${item.description || 'este artículo'}, ¿es nuevo o usado y cuál es su valor?`}
          onSend={async (texto) => {
            await onAskClient(`items.${item.id}`, texto);
            setPreguntaEnviada(true);
          }}
        />
        {preguntaEnviada && (
          <span
            className="inline-flex items-center gap-0.5 text-[10px]"
            style={{ color: 'var(--green)' }}
            role="status"
          >
            <Check size={10} aria-hidden="true" /> Pregunta enviada
          </span>
        )}
        {confirmarQuitar ? (
          <button
            type="button"
            onClick={() => onQuitar && onQuitar()}
            aria-label="Confirmar eliminación del artículo"
            className="rounded-md border px-1.5 py-1 text-[10px] font-semibold
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)]
                       hover:opacity-90"
            style={{ background: 'var(--red-soft)', borderColor: 'var(--avianca-red)', color: 'var(--avianca-red)' }}
          >
            Confirmar
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmarQuitar(true)}
            aria-label="Eliminar artículo"
            title="Eliminar artículo"
            className="inline-flex items-center justify-center rounded-md p-1.5
                       hover:bg-[var(--red-soft)]
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)]"
            style={{ color: 'var(--text-tertiary)' }}
          >
            <Trash2 size={13} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ItemRow;

/*
Ejemplo de uso (dentro de PackageBlock):
  <ItemRow
    item={it}
    sourceTimestamp={timestampsPorMensaje[it.source_message_id]}
    onChange={(campo, valor) => actualizarItem(it.id, campo, valor)}
    onQuitar={() => quitarItem(it.id)}
    onEnterNewRow={() => agregarItemManual()}
    quoteBuffer={quoteBuffer}
    onConsumeQuote={onConsumeQuote}
    onJumpToMessage={onJumpToMessage}
    onAskClient={onAskClient}
    autoFocus={it.id === ultimoAgregadoId}
    isNew={it.id === ultimoAgregadoId}
  />
*/
